import { useTranslations } from 'next-intl'
import { toast } from 'sonner'
import { BulkDeleteResponse } from '@/types/general'

/**
 * Handles bulk delete success response with toast notifications
 * @param response - The bulk delete response from the backend
 * @param t - Translation function
 */
export const handleBulkDeleteSuccess = (response: BulkDeleteResponse, t: (key: string, values?: any) => string) => {
  const successCount = response.deletedItems.length
  const failCount = Object.keys(response.failedItems).length

  if (failCount === 0) {
    // All items were deleted successfully
    toast.success(t('bulk_delete_success', { count: successCount }))
  } else if (successCount > 0) {
    // Some items were deleted, but some failed
    toast.success(t('bulk_delete_partial_success', { successCount, failCount }))

    // Show error details for failed items
    Object.entries(response.failedItems).forEach(([id, errorMessage]) => {
      toast.error(`${id}: ${errorMessage}`)
    })
  } else {
    // All items failed to delete
    toast.error(t('bulk_delete_failed_items'))

    // Show error details for failed items
    Object.entries(response.failedItems).forEach(([id, errorMessage]) => {
      toast.error(`${id}: ${errorMessage}`)
    })
  }
}

/**
 * Creates a bulk delete success handler with translation support
 * @returns A function that handles bulk delete success
 */
export const useBulkDeleteSuccessHandler = () => {
  const t = useTranslations()

  return (response: BulkDeleteResponse) => {
    handleBulkDeleteSuccess(response, t)
  }
}
